// The thread page's (and the email drawer's) view of "Remember for me" (U6): your private note
// first, then what was promised on this thread, yours and theirs. With `bindKeys`, Shift+N writes
// the note (the thread page; on a card the card binds it).
import { useState } from "react";
import { Handshake, NotebookPen } from "lucide-react";
import { useThreadMemory } from "@web/lib/rememberApi";
import { useCommand } from "@web/shell/commands";
import { cn } from "@web/lib/cn";
import { Kbd } from "@web/ui/Kbd";
import { ThreadNote } from "./ThreadNote";
import { theirPromiseLine } from "./wording";

export function ThreadMemory({
  threadKey,
  bindKeys = false,
  className,
}: {
  threadKey: string | null;
  bindKeys?: boolean;
  className?: string;
}) {
  const memory = useThreadMemory(threadKey);
  const [editing, setEditing] = useState(false);
  useCommand("thread.note", () => setEditing(true), bindKeys && !!threadKey);
  const view = memory.data;
  if (!threadKey || !view) return null;
  const lines = [
    ...view.promises.map((p) =>
      p.holding ? `You said you'd reply ${p.whenText}` : `You said: “${p.quote}”`,
    ),
    ...(view.theirPromise ? [theirPromiseLine(view.theirPromise.quote)] : []),
  ];
  const offerNote = bindKeys && !view.note && !editing;
  if (!view.note && !editing && !lines.length && !offerNote) return null;
  return (
    <div className={cn("flex flex-col gap-2", className)} data-testid="thread-memory">
      <ThreadNote
        threadKey={threadKey}
        note={view.note}
        editing={editing}
        onEditingChange={setEditing}
        {...(bindKeys ? { shortcut: "⇧N" } : {})}
      />
      {offerNote ? (
        <button
          type="button"
          className="inline-flex items-center gap-1.5 self-start text-sm text-muted hover:text-ink"
          onClick={() => setEditing(true)}
        >
          <NotebookPen aria-hidden className="size-4" />
          Add a private note
          <Kbd>⇧N</Kbd>
        </button>
      ) : null}
      {lines.map((line) => (
        <p key={line} className="flex items-start gap-1.5 text-sm text-ink-2">
          <Handshake aria-hidden className="mt-0.5 size-4 shrink-0 text-muted" />
          <span className="min-w-0 break-words">{line}</span>
        </p>
      ))}
    </div>
  );
}
